import { components } from "./schema/api-schema";
import { MarketData } from "./types/MarketData";

export type WatchlistItems = components["schemas"]["WatchlistItems"];
export type Watchlist = components["schemas"]["Watchlist"];
export type PerformanceVsMarketChartItem =
  components["schemas"]["PerformanceVsMarketChartItem"];
export type RiskReturnWatchlistItem =
  components["schemas"]["RiskReturnChartItem"];
export type BestAndWorstDaysItem =
  components["schemas"]["BestAndWorstDaysItem"];
export type LargestVolMoveItem =
  components["schemas"]["LargestVolMoveChartItem"];

export type InstrumentNews = {
  title: string;
  date: string;
  link: string;
  content: string;
};

export type TriggerInfo = {
  label: string;
  dataLessLabel?: string;
  info?: string;
};

export type DisplayItem = {
  id: string;
  instrumentId: string;
  label: string;
  value: number;
  name: string;
  icon: string;
  price?: number;
  strategyLabels?: string[];
  triggers?: TriggerInfo[];
  strategiesCount?: number;
  strategies?: any; // For income generation data (topCalls, topPuts, allCalls, allPuts)
  triggerStrategies?: any[];
  generalStrategies?: any[];
  metadata?: any;
};

export type MarketInsightsSortType = {
  categoryId: string;
  subCategoryId: string;
  subCategoryLabel?: string;
  subSubCategoryLabel?: string;
  isAll?: boolean;
  period?: string | undefined;
};

export type WatchlistMarketItem = {
  id: string;
  title: string;
  default: boolean;
  userMarket: boolean;
  portfolioId: string;
  originalPortfolioId: string;
};

interface ChartItem {
  subTitle: string;
  whyImportant: string;
  rows: Row[];
  children: Child[];
}

interface Row {
  label: string;
  instruments: Instrument[];
}

interface Instrument {
  instrumentId: number;
  name: string;
  label: string;
  value: string;
  icon: string | null;
}

interface Child {
  title: string;
  whyImportant: string;
  rows: Row[];
}
export type WatchlistBreakdown = {
  id: string;
  title: string;
  chart: ChartItem[];
};

export type SortCategoriesGetter = (
  environment: string | undefined
) => Promise<SortCategory[]> | SortCategory[];
export type SortedItemDetails = {
  title: string;
  rows: { instruments: DisplayItem[]; label: string }[];
  description?: string;
};
export type SortedItemGetter = (
  sort: MarketInsightsSortType,
  watchlistId: string,
  env: string | undefined,
  period?: string
) => Promise<SortedItemDetails> | SortedItemDetails;

export type InstrumentSearch = (
  search: string,
  environment: string | undefined,
  token: string
) => Promise<InstrumentSearchResult[]>;
export type InstrumentSearchResult = {
  instrumentId: string;
  countryName: string;
  countryCode: string;
  name: string;
  exchange: string;
  iconUrl: string;
  symbol: string;
  type: "Shares" | "Portfolio";
};

export type SortCategory = {
  categoryId: string;
  title: string;
  categoryItems: SortCategoryItem[];
};

interface Child {
  title: string;
  subTitles: string[];
}

export interface SortCategoryItem {
  title: string;
  itemId: string;
  subTitles: string[] | null;
  children: Child[];
}

export enum SortCategoryId {
  PERFORMANCE = "PERFORMANCE",
  RISK = "RISK",
  TECHNICALS = "TECHNICALS",
  FUNDAMENTALS = "FUNDAMENTALS",
  OPTIONS = "OPTIONS",
  TRADE_IDEAS = "TRADE_IDEAS",
  BONDS = "BONDS",
  EARNINGS = "EARNINGS",
  ALERTS = "ALERTS",
}

export type OptionStatItem = {
  desc: string;
  sub: string;
  stats: {
    s: string[];
    sp: string;
  }[];
};

export type OptionStatDto = {
  title: string;
  stats: OptionStatItem[];
};

export type InsightStatDto = {
  stat: string;
  rating: string;
  detailInsightId: string;
};

export type InsightDto = {
  title: string;
  stats: InsightStatDto[];
};

export type InstrumentInfo = {
  ccy: {
    code: string;
    label: string;
  };
  id: number;
  name: string;
  symbol: string;
  type: string;
  closePrice: number;
  icon: string;
  totalReturn1yPct: number;
  description?: string;
  betaRoll1y: number;
  correl: number;
  iconUrl: string;
  riskReturn1yAbs: number;
  rsi: number;
  sourceId: string;
  totalReturn1mPct: number;

  totalReturn3mPct: number;
  totalReturn3yPct: number;
  totalReturn6mPct: number;
  totalReturnYTDPct: number;
  vol1dDailyEquivPct: number;
  vol1yPct: number;
};

type InstrumentBase = {
  id: number;
  name: string;
  description: string | null;
  symbol: string;
  sourceId: string;
  iconUrl: string | null;
  type: string;
  ccySymbol: {
    code: string;
    label: string;
  };
};

type InstrumentTimeseriesItem = {
  date: string;
  closepx_abs: number | null;
  targetprice_abs: number | null;
  sma_50d_abs: number;
  sma_100d_abs: number;
  sma_200d_abs: number;
  high: number;
  low: number;
  open: number;
  sma_20d_abs: number;
  upperbollingerband2std_20d_abs: number;
  upperbollingerband3std_20d_abs: number;
  lowerbollingerband2std_20d_abs: number;
  lowerbollingerband3std_20d_abs: number;
};

export type InstrumentSummaryDetailed = {
  instrument: InstrumentBase;
  sector: InstrumentBase;
  benchMark: InstrumentBase;
  instrumentData: {
    industry: string;
    vol_100d_pct: number;
    betaroll_1y_abs: number;
    marketCapitalisation: number;
    targetprice_abs: number;
    closepx_abs: number;
    peRatio: number;
    rsi_14d_abs: number;
    maxpxabs_1y_abs: number;
    minpxabs_1y_abs: number;
    correlationroll_1y_abs: number;
    divyield_1y_pct: number;
    analystrating_stat: string;
    minDate: string;
    bondDerivedData: {
      couponRate: number;
      date: string;
      durationInYears: number;
      runningYieldPct: number;
      secId: number;
      timeToMaturityInMonths: number;
      yieldToCall: number;
      yieldToMaturity: number;
      yieldToWorse: number;
    };
  };
  instrumentTimeseries: InstrumentTimeseriesItem[];
};

export type InstrumentSummary = {
  insights: InsightDto[];
  instrument: InstrumentInfo;
  news: InstrumentNews[];
};

export type OptionInsights = {
  stats: OptionStatDto[];
};

export interface AnalystRatingHistoryPoint {
  quarterLabel?: string;
  date: string;
  price: number;
  rating: number;
  buy: number | null;
  outperform: number | null;
  hold: number | null;
  underperform: number | null;
  sell: number | null;
}
export type AnalystRating = {
  name: string;
  symbol: string;
  overallAnalystRating: string;
  recentAnalystRating: string;
  recentAnalystRatingDate: string;
  currentDate: string;
  targetPrice: number;
  rating: number;
  previousRating: number;
  currency: string;
  description: string;
  analystRatingHistory: Array<AnalystRatingHistoryPoint>;
  analystRatingQuarterlyHistory: Array<AnalystRatingHistoryPoint>;
  priceChangeAfterAnalystRatingChange: {
    improvedRating: {
      oneDayReturn: ReturnMetrics;
      oneWeekReturn: ReturnMetrics;
      oneMonthReturn: ReturnMetrics;
    };
    declinedRating: {
      oneDayReturn: ReturnMetrics;
      oneWeekReturn: ReturnMetrics;
      oneMonthReturn: ReturnMetrics;
    };
  };
  analystRatingHeader: string;
  analystRatingFooter: string;
  priceChangeStats: string[];
};

type ReturnMetrics = {
  name: string;
  win: number;
  avg: number;
  max: number;
};

export type ITooltipItem = {
  label: string;
  value: string;
};

export type IChartDataItemV2 = {
  name: string;
  value: number;
  color: string;
  id: string;
  category: string;
  tooltipExtra?: ITooltipItem[];
  data?: [string, number][];
  date?: Date | string;
  opacity?: number;
};

export interface WhatIfPortfolioResponse {
  basePortfolioId: string;
  whatIfPortfolioId: string;
  originalPortfolioId: string;
}

export interface MarketCategory {
  watchlist: {
    displayName: string;
    id: string;
  };
  insight: InsightCategory[];
}

interface InsightCategory {
  id: string;
  title: string;
  chart: ChartItem[];
}

interface ChartItem {
  subTitle: string;
  whyImportant: string;
  rows: Row[];
  children: Child[];
}

interface Row {
  label: string;
  instruments: Instrument[];
}

interface Instrument {
  instrumentId: number;
  name: string;
  label: string;
  value: string;
  icon: string | null;
}

interface Child {
  title: string;
  whyImportant: string;
  rows: Row[];
}

interface HeaderLabel {
  name: string;
  value: string;
}

interface WinAverageMax {
  label: string;
  win: number;
  avg: number;
  max: number;
}

export interface SummaryEarnings {
  headerLabels: HeaderLabel[];
  earningStatsTop: string;
  earningStatsBottom: string[];
  beforeWinAverageMax: WinAverageMax[];
  afterWinAverageMax: WinAverageMax[];
}

export interface SupriseAndPriceHistoryPoint {
  date: string;
  price: number;
  surprisePct: number | null;
  priceChange: number;
}

export interface EarningsData {
  period: string;
  surpriseAndReturn: { date: string; returnValue: number }[];
  surpriseStats: {
    header: string;
    stats: string[];
  };
}

export type SupriseAndPriceHistoryResponse = {
  priceDates: SupriseAndPriceHistoryPoint[];
  earningsData: EarningsData[];
};
export type SupriseAndPriceHistory = SupriseAndPriceHistoryResponse;

//income generation

export enum OutOfTheMoney {
  AT_THE_MONEY = "AT THE MONEY",
  "5_OUT_OF_THE_MONEY" = "5% OUT OF THE MONEY",
  "10_OUT_OF_THE_MONEY" = "10% OUT OF THE MONEY",
  "15_OUT_OF_THE_MONEY" = "15% OUT OF THE MONEY",
}

export enum Time {
  ONE_MONTH = "1 month",
  THREE_MONTHS = "3 month",
  SIX_MONTHS = "6 month",
}
export enum Badge {
  Bull = "Bull",
  Bear = "Bear",
  Neutral = "Neutral",
  Income = "Income",
}

type Operation = {
  operationType: "long" | "short";
  optionType: "call" | "put";
  outOfTheMoney: OutOfTheMoney;
  stockQuantity?: number;
};
export type OptionData = {
  strategyType: StrategyType;
  operations: Operation[];
  time: Time;
  stockQuantity?: number;
  badge?: Badge;
};
export enum StrategyType {
  IncomeShortPut = "income-short-put",
  CashExtraction = "cash-extraction",
  Hedging = "hedging",
  SyntheticYield = "synthetic-yield",
  Calls = "calls",
  Puts = "puts",
  CallsSpreads = "calls-spreads",
  PutsSpreads = "puts-spreads",
}
// Type for the transformed MarketData with numeric values
export type NumericMarketData = {
  [K in keyof MarketData]: K extends
    | "icon"
    | "name"
    | "label"
    | "Sector"
    | "instrumentId"
    ? MarketData[K] // Keep these fields as they are
    : MarketData[K] extends string | null
    ? number
    : MarketData[K];
};
export type Stock = {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
  volume: string;
  marketCap: string;
  pe?: number;
  beta?: number;
  dividend?: number;
  sector: string;
  metadata: NumericMarketData;
};

export type Trigger = {
  name: string;
  triggers: { label: string; dataLessLabel?: string; info?: string }[];
  strategies: Strategy[];
  generalStrategies: Strategy[];
  topPuts: Strategy[];
  topCalls: Strategy[];
  incomeGeneration?: {
    topCalls: Strategy[];
    topPuts: Strategy[];
    allCalls: Strategy[];
    allPuts: Strategy[];
  };
  icon?: string;
  price: number;
  metadata: any;
};

export type Strategy = {
  id: string;
  subtitle: string;
  description: string;
  value: boolean;
  optionData: OptionData;
  badge?: Badge;
  label?: string;
};
// Type for the output with instrument info and matching triggers
export type InstrumentWithTriggers = {
  name: string;
  icon: string;
  price: number;
  triggers: { label: string; dataLessLabel?: string; info?: string }[];
  strategies: Strategy[];
  generalStrategies: Strategy[];
  topPuts: Strategy[];
  topCalls: Strategy[];
  incomeGeneration?: {
    topCalls: Strategy[];
    topPuts: Strategy[];
    allCalls: Strategy[];
    allPuts: Strategy[];
  };
  marketCap: number;
  triggerStrategies: Strategy[];
  metadata: any; // Original instrument data
};
